import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Box,
  Chip,
} from '@mui/material';

interface EventSummary {
  event_category: string;
  event_name: string;
  count: number;
}

interface RecentEvent {
  event_name: string;
  event_category: string;
  event_data: Record<string, unknown> | null;
  page_path: string | null;
  timestamp: string;
  event_value: number | null;
}

interface EventsData {
  summary: EventSummary[];
  recent: RecentEvent[];
}

interface EventsTableProps {
  data: EventsData | null;
  loading: boolean;
}

const getCategoryColor = (category: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
    feature: 'primary',
    engagement: 'success',
    interaction: 'info',
    navigation: 'secondary',
    error: 'error',
    conversion: 'warning',
  };
  return colors[category.toLowerCase()] || 'primary';
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const EventsTable: React.FC<EventsTableProps> = ({ data, loading }) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Events
        </Typography>

        {loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={40} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : !data || (data.summary.length === 0 && data.recent.length === 0) ? (
          <Typography color="text.secondary">No events recorded yet</Typography>
        ) : (
          <>
            {data.summary.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Event Summary
                </Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.summary.slice(0, 10).map((event, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {event.event_name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.event_category}
                              size="small"
                              color={getCategoryColor(event.event_category)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{event.count.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {data.recent.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Recent Events
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Page</TableCell>
                        <TableCell align="right">Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recent.slice(0, 15).map((event, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {event.event_name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.event_category}
                              size="small"
                              color={getCategoryColor(event.event_category)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {event.page_path || '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatTimestamp(event.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EventsTable;
